import { Button } from "@astryxdesign/core";
import { useEffect, useRef, useState } from "react";
import { onPilotOpen } from "@/lib/pilot";

declare const __API_BASE__: string | undefined;

/**
 * Where the signup goes. The intake API already has a contact endpoint that
 * renders the shared email chrome and posts it to BUSINESS_EMAIL over Resend
 * (`go-service/internal/api/contact.go`), and its CORS allowlist already names
 * this origin. So the pilot form is that endpoint with a fixed subject rather
 * than a second path to the same inbox.
 */
const API_BASE =
  (typeof __API_BASE__ !== "undefined" && __API_BASE__) || "http://localhost:8080";

/** The endpoint requires a name and a message; a request to be contacted has neither. */
const NAME = "Try for free";
// Plain ASCII: the subject crosses Resend and lands in a mail client, and an
// em dash there is one encoding hop away from a replacement character.
const SUBJECT = "Try for free - hireos.co.nz";
const MESSAGE = "Asked to be contacted from the landing page.";

type Phase = "asking" | "sending" | "sent" | "failed";

/**
 * The try-for-free dialog: one email field, and what we do next.
 *
 * Mounted once at the foot of the page and opened by any of the "Try for
 * free" links (see lib/pilot.ts). It asks for the one thing we need to get
 * back to them and nothing else: every field beyond the address is one more
 * reason to close the tab.
 */
export function PilotDialog() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>("asking");
  const field = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => onPilotOpen(() => setOpen(true)), []);

  // Opening resets whatever the last visit left behind, holds the page still
  // under the dialog, and puts the cursor in the field.
  useEffect(() => {
    if (!open) return;
    setPhase("asking");
    document.body.classList.add("menu-open");
    const id = requestAnimationFrame(() => field.current?.focus());

    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", key);
    return () => {
      document.body.classList.remove("menu-open");
      document.removeEventListener("keydown", key);
      cancelAnimationFrame(id);
    };
  }, [open]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;

    setPhase("sending");
    try {
      const res = await fetch(new URL("/api/contact", API_BASE), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: NAME,
          email: address,
          subject: SUBJECT,
          message: MESSAGE,
        }),
      });
      setPhase(res.ok ? "sent" : "failed");
    } catch {
      setPhase("failed");
    }
  }

  if (!open) return null;

  return (
    <div
      className="pilot"
      onMouseDown={(e) => {
        if (!panel.current?.contains(e.target as Node)) setOpen(false);
      }}
    >
      <div className="pbox" ref={panel} role="dialog" aria-modal="true" aria-label="Try for free">
        <button className="pclose" type="button" aria-label="Close" onClick={() => setOpen(false)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        {phase === "sent" ? (
          <>
            <h2>Thanks, we'll be in touch.</h2>
            <p className="psub">
              We'll email {email.trim()} soon.
            </p>
            <Button label="Close" variant="ghost" width="100%" onClick={() => setOpen(false)} />
          </>
        ) : (
          <>
            <h2>Try for free.</h2>
            <p className="psub">
              Leave your email and we'll get back to you to show you round and set your number
              up.
            </p>
            <form onSubmit={send}>
              <label htmlFor="pilot-email">Your email</label>
              <input
                id="pilot-email"
                ref={field}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                label="Send"
                variant="ghost"
                width="100%"
                type="submit"
                isLoading={phase === "sending"}
              />
              {phase === "failed" && (
                <p className="perr" role="alert">
                  That didn't send. Email{" "}
                  <a href="mailto:hello@loony.io">hello@loony.io</a> and we'll pick it
                  up from there.
                </p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
